import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  MapPin, Cloud, Sun, CloudRain, Wind, Droplets,
  Thermometer, ChevronRight, Star, Users, Waves,
  TreePine, Fish, Mountain, Turtle
} from "lucide-react";
import Navbar from "../../components/Header";
import NgazidjaImg from "../data/ngazidja.jpeg";
import AnjouanImg  from "../data/anjouan.jpeg";
import MoheliImg   from "../data/moheli.jpeg";

const G      = "var(--k-green)";
const G_DARK = "var(--k-green-hover)";
const G_LIGHT = "var(--k-green-soft)";
const API    = "https://api.komorotel.com";

/* ─────────────────────────────────────────────────
   DONNÉES STATIQUES DES ÎLES
───────────────────────────────────────────────── */
const ISLANDS = [
  {
    slug:    "Ngazidja",
    name:    "Ngazidja",
    aka:     "Grande Comore",
    image:   "https://www.vanilla-islands.org/wp-content/uploads/2015/12/accueil-comores-plage-barque.jpg",
    lat:     -11.7,
    lon:     43.3,
    capital: "Moroni",
    desc:    "La plus grande île de l'archipel, dominée par le Karthala, l'un des volcans actifs les plus importants du monde. Moroni, la capitale, offre un charme oriental unique avec sa médina et son ancien port.",
    highlights: [
      { icon: <Mountain size={16} />, label: "Volcan Karthala" },
      { icon: <Waves size={16} />,    label: "Plages de sable blanc" },
      { icon: <MapPin size={16} />,   label: "Médina de Moroni" },
    ],
    tags: ["Capitale", "Volcanique", "Culture"],
    color: "#065f46",
    gradient: "linear-gradient(135deg, #065f46 0%, #047857 100%)",
  },
  {
    slug:    "Anjouan",
    name:    "Anjouan",
    aka:     "Ndzuani",
    image:   "https://www.nomade-aventure.com/Content/Images/ImgProduits/COM/11221/701448_QuatreTiers.ori.jpg",
    lat:     -12.21,
    lon:     44.43,
    capital: "Mutsamudu",
    desc:    "Surnommée «l'île aux parfums», Anjouan est couverte de forêts tropicales denses et de plantations d'ylang-ylang. Ses montagnes verdoyantes et ses cascades en font un paradis pour les randonneurs.",
    highlights: [
      { icon: <TreePine size={16} />, label: "Forêts tropicales" },
      { icon: <Waves size={16} />,    label: "Cascades & rivières" },
      { icon: <Star size={16} />,     label: "Ylang-ylang & épices" },
    ],
    tags: ["Nature", "Randonnée", "Parfums"],
    color: "#1e40af",
    gradient: "linear-gradient(135deg, #1e3a5f 0%, #1e40af 100%)",
  },
  {
    slug:    "Moheli",
    name:    "Mohéli",
    aka:     "Mwali",
    image:   MoheliImg,
    lat:     -12.34,
    lon:     43.73,
    capital: "Fomboni",
    desc:    "La plus petite et la plus préservée des îles comoriennes. Classée parc marin national, Mohéli est le sanctuaire des tortues vertes et des baleines à bosse. Un écrin de nature vierge.",
    highlights: [
      { icon: <Turtle size={16} />, label: "Tortues vertes" },
      { icon: <Fish size={16} />,   label: "Plongée & snorkeling" },
      { icon: <Waves size={16} />,  label: "Parc marin national" },
    ],
    tags: ["Écotourisme", "Plongée", "Préservé"],
    color: "#0e7490",
    gradient: "linear-gradient(135deg, #0e4f5c 0%, #0e7490 100%)",
  },
];

/* Icône météo selon code WMO */
function WeatherIcon({ code, size = 20 }) {
  if (code === 0)                    return <Sun size={size} color="#f59e0b" />;
  if (code <= 3)                     return <Cloud size={size} color="#9ca3af" />;
  if (code >= 51 && code <= 67)      return <CloudRain size={size} color="#60a5fa" />;
  if (code >= 80 && code <= 82)      return <CloudRain size={size} color="#60a5fa" />;
  return <Cloud size={size} color="#9ca3af" />;
}

function WeatherLabel({ code }) {
  if (code === 0)               return "Ensoleillé";
  if (code <= 3)                return "Nuageux";
  if (code >= 51 && code <= 67) return "Pluvieux";
  if (code >= 80 && code <= 82) return "Averses";
  return "Variable";
}

/* ─────────────────────────────────────────────────
   WIDGET MÉTÉO (Open-Meteo, sans clé API)
───────────────────────────────────────────────── */
function WeatherWidget({ lat, lon }) {
  const [weather, setWeather] = useState(null);

  useEffect(() => {
    fetch(
      `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}` +
      `&current=temperature_2m,weathercode,windspeed_10m,relativehumidity_2m` +
      `&timezone=auto&forecast_days=1`
    )
      .then(r => r.json())
      .then(d => setWeather(d.current))
      .catch(() => {});
  }, [lat, lon]);

  if (!weather) return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", background: "rgba(255,255,255,0.12)", borderRadius: 10 }}>
      <div style={{ width: 40, height: 16, background: "rgba(255,255,255,0.2)", borderRadius: 4 }} />
    </div>
  );

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "10px 14px", background: "rgba(255,255,255,0.12)", borderRadius: 10, backdropFilter: "blur(8px)" }}>
      <WeatherIcon code={weather.weathercode} size={22} />
      <div>
        <div style={{ fontSize: 18, fontWeight: 800, color: "#fff", lineHeight: 1 }}>
          {Math.round(weather.temperature_2m)}°C
        </div>
        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.75)" }}>
          <WeatherLabel code={weather.weathercode} />
        </div>
      </div>
      <div style={{ borderLeft: "1px solid rgba(255,255,255,0.2)", paddingLeft: 14, display: "flex", flexDirection: "column", gap: 3 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "rgba(255,255,255,0.75)" }}>
          <Wind size={11} /> {Math.round(weather.windspeed_10m)} km/h
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "rgba(255,255,255,0.75)" }}>
          <Droplets size={11} /> {weather.relativehumidity_2m}%
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────
   CARD ÎLE
───────────────────────────────────────────────── */
function IslandCard({ island, count }) {
  const [hovered, setHovered] = useState(false);
  const navigate = useNavigate();

  return (
    <div
      style={{
        borderRadius: 20, overflow: "hidden", cursor: "pointer",
        boxShadow: hovered ? "0 20px 60px rgba(0,0,0,0.18)" : "0 4px 20px rgba(0,0,0,0.08)",
        transform: hovered ? "translateY(-4px)" : "none",
        transition: "all 0.3s ease", background: "#fff",
      }}
      onClick={() => navigate(`/hotels?destination=${island.slug}`)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}>

      {/* Image hero */}
      <div style={{ position: "relative", height: 280, overflow: "hidden" }}>
        <img src={island.image} alt={island.name}
          style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.5s ease", transform: hovered ? "scale(1.06)" : "scale(1)" }} />
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.2) 50%, rgba(0,0,0,0.05) 100%)" }} />

        {/* Tags */}
        <div style={{ position: "absolute", top: 16, left: 16, display: "flex", gap: 6, flexWrap: "wrap" }}>
          {island.tags.map(tag => (
            <span key={tag} style={{ background: "rgba(255,255,255,0.2)", backdropFilter: "blur(8px)", color: "#fff", fontSize: 10, fontWeight: 700, padding: "3px 10px", borderRadius: 20, letterSpacing: "0.04em", border: "1px solid rgba(255,255,255,0.3)" }}>
              {tag}
            </span>
          ))}
        </div>

        {/* Météo */}
        <div style={{ position: "absolute", top: 16, right: 16 }}>
          <WeatherWidget lat={island.lat} lon={island.lon} />
        </div>

        {/* Nom + météo */}
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "20px 24px" }}>
          <h2 style={{ fontFamily: "Georgia, serif", fontSize: 28, fontWeight: 700, color: "#fff", lineHeight: 1.1, marginBottom: 2 }}>
            {island.name}
          </h2>
          <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 13, color: "rgba(255,255,255,0.8)" }}>
            <MapPin size={13} />{island.capital} · {island.aka}
          </div>
        </div>
      </div>

      {/* Contenu */}
      <div style={{ padding: "22px 24px" }}>
        <p style={{ fontSize: 14, color: "#374151", lineHeight: 1.75, marginBottom: 18 }}>
          {island.desc}
        </p>

        {/* Highlights */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
          {island.highlights.map((h, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 30, height: 30, borderRadius: "50%", background: G_LIGHT, display: "flex", alignItems: "center", justifyContent: "center", color: G, flexShrink: 0 }}>
                {h.icon}
              </div>
              <span style={{ fontSize: 13, color: "#374151", fontWeight: 500 }}>{h.label}</span>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: 18, borderTop: "1px solid #f3f4f6" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ width: 32, height: 32, borderRadius: "50%", background: G_LIGHT, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Users size={14} color={G} />
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 800, color: "#111827" }}>{count ?? "—"}</div>
              <div style={{ fontSize: 11, color: "#9ca3af" }}>hébergements</div>
            </div>
          </div>
          <button
            style={{ display: "flex", alignItems: "center", gap: 7, background: G, color: "#fff", border: "none", borderRadius: 10, padding: "10px 18px", fontSize: 13, fontWeight: 700, cursor: "pointer", transition: "background 0.18s" }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = G_DARK}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = G}>
            Voir les hébergements <ChevronRight size={15} />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────
   SECTION MEILLEURE PÉRIODE
───────────────────────────────────────────────── */
const MONTHS = ["Jan","Fév","Mar","Avr","Mai","Jun","Jul","Aoû","Sep","Oct","Nov","Déc"];
const BEST_MONTHS = [4, 5, 6, 7, 8, 9]; // Mai → Oct (index 0-based)

function BestTimeSection() {
  const current = new Date().getMonth();
  return (
    <div style={{ background: "#fff", borderRadius: 20, padding: "32px 32px", border: "1px solid #f3f4f6", boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}>
      <h3 style={{ fontFamily: "Georgia, serif", fontSize: 20, fontWeight: 700, color: "#111827", marginBottom: 6 }}>
        Meilleure période pour visiter
      </h3>
      <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 24, lineHeight: 1.6 }}>
        Les Comores ont deux saisons : une saison sèche et fraîche de <strong>mai à novembre</strong> (idéale pour voyager) et une saison chaude et humide de décembre à avril.
      </p>
      <div style={{ display: "flex", gap: 6 }}>
        {MONTHS.map((m, i) => {
          const isBest    = BEST_MONTHS.includes(i);
          const isCurrent = i === current;
          return (
            <div key={i} style={{ flex: 1, textAlign: "center" }}>
              <div style={{
                height: 40, borderRadius: 8, marginBottom: 6,
                background: isBest ? (isCurrent ? G : `rgb(var(--k-green-rgb)/0.19)`) : "#f3f4f6",
                border: isCurrent ? `2px solid ${G}` : "2px solid transparent",
                transition: "all 0.2s",
              }} />
              <div style={{ fontSize: 10, fontWeight: isCurrent ? 700 : 400, color: isCurrent ? G : "#9ca3af" }}>{m}</div>
            </div>
          );
        })}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{ width: 12, height: 12, borderRadius: 3, background: `rgb(var(--k-green-rgb)/0.19)` }} />
          <span style={{ fontSize: 12, color: "#6b7280" }}>Idéal</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{ width: 12, height: 12, borderRadius: 3, background: "#f3f4f6" }} />
          <span style={{ fontSize: 12, color: "#6b7280" }}>Saison humide</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{ width: 12, height: 12, borderRadius: 3, background: G, border: `2px solid ${G}` }} />
          <span style={{ fontSize: 12, color: "#6b7280" }}>Mois actuel</span>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────
   PAGE DESTINATIONS
───────────────────────────────────────────────── */
export default function Destinations() {
  const navigate = useNavigate();
  const [counts, setCounts] = useState({});

  useEffect(() => {
    fetch(`${API}/api/hotels/countByIsland?islands=Ngazidja,Anjouan,Moheli`)
      .then(r => r.json())
      .then(d => {
        if (Array.isArray(d)) setCounts({ Ngazidja: d[0], Anjouan: d[1], Moheli: d[2] });
      })
      .catch(() => {});
  }, []);

  const totalCount = Object.values(counts).reduce((a, b) => a + (b || 0), 0);

  return (
    <div style={{ minHeight: "100vh", background: "#f9fafb", fontFamily: "'DM Sans','Inter',system-ui,sans-serif" }}>
      <Navbar />

      {/* ── Hero ── */}
      <div style={{ background: `linear-gradient(135deg, #0a2810 0%, #0f4020 50%, #0a3518 100%)`, paddingTop: 96, paddingBottom: 64 }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 32px", textAlign: "center" }}>
          <div style={{ display: "inline-flex", alignItems: "center", background: "rgba(58,157,35,0.25)", border: "1px solid rgba(58,157,35,0.4)", color: `${G}`, fontSize: 11, fontWeight: 800, letterSpacing: "0.09em", padding: "5px 14px", borderRadius: 20, marginBottom: 20 }}>
            🇰🇲 ARCHIPEL DES COMORES
          </div>
          <h1 style={{ fontFamily: "Georgia, serif", fontSize: 48, fontWeight: 700, color: "#fff", lineHeight: 1.15, marginBottom: 16 }}>
            Choisissez votre île
          </h1>
          <p style={{ fontSize: 16, color: "rgba(255,255,255,0.72)", maxWidth: 560, margin: "0 auto 32px", lineHeight: 1.7 }}>
            Trois îles, trois ambiances. De la capitale animée aux forêts tropicales en passant par le sanctuaire marin le plus préservé d'Afrique.
          </p>
          {/* Stats */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 32, flexWrap: "wrap" }}>
            {[
              { val: "3", label: "Îles à explorer" },
              { val: totalCount || "120+", label: "Hébergements" },
              { val: "28°C", label: "Température moyenne" },
            ].map((s, i) => (
              <div key={i} style={{ textAlign: "center" }}>
                <div style={{ fontFamily: "Georgia, serif", fontSize: 28, fontWeight: 700, color: "#fff" }}>{s.val}</div>
                <div style={{ fontSize: 12, color: "rgba(255,255,255,0.55)" }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "48px 32px 64px" }}>

        {/* ── Cards îles ── */}
        <div className="dest-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 24, marginBottom: 40 }}>
          {ISLANDS.map(island => (
            <IslandCard key={island.slug} island={island} count={counts[island.slug]} />
          ))}
        </div>

        {/* ── Meilleure période ── */}
        <BestTimeSection />

        {/* ── CTA ── */}
        <div style={{ marginTop: 32, background: G, borderRadius: 20, padding: "36px 40px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 20 }}>
          <div>
            <h3 style={{ fontFamily: "Georgia, serif", fontSize: 22, fontWeight: 700, color: "#fff", marginBottom: 6 }}>
              Pas encore décidé ?
            </h3>
            <p style={{ fontSize: 14, color: "rgba(255,255,255,0.8)" }}>
              Explorez tous nos hébergements et filtrez par île, type et budget.
            </p>
          </div>
          <button onClick={() => navigate("/hotels")}
            style={{ background: "#fff", color: G, border: "none", borderRadius: 12, padding: "13px 28px", fontSize: 14, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap", transition: "opacity 0.18s", display: "flex", alignItems: "center", gap: 8 }}
            onMouseEnter={e => e.currentTarget.style.opacity = "0.88"}
            onMouseLeave={e => e.currentTarget.style.opacity = "1"}>
            Voir tous les hébergements <ChevronRight size={16} />
          </button>
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .dest-grid { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 600px) {
          .dest-hero h1 { font-size: 32px !important; }
        }
      `}</style>
    </div>
  );
}